<script module lang="ts">
  import type { Alert } from '../api/types'

  /**
   * What is known before the text arrives. The map menu's `AlertFeature`
   * carries a product name and an expiry; a notification carries the id alone.
   */
  export interface AlertRef {
    capId: string
    prodType?: string
    expiration?: string | null
    /**
     * The alert as the forecast bundle holds it, when an alert row opened the
     * popup: shown at once, then replaced by the fetch.
     */
    alert?: Alert
  }
</script>

<script lang="ts">
  import { ApiError, fetchAlert } from '../api/client'
  import { alertLevel, areaList, capTail, paragraphs } from '../alertText'
  import TextPopup, { type PopupSection } from './TextPopup.svelte'

  /**
   * The full text of one alert, opened from the map menu, an alert row, or by
   * its CAP id from a notification or a link.
   *
   * A Svelte component, deliberately not MapLibre's `Popup`, which takes an
   * HTML *string*. `description` and `instruction` are the longest, least
   * trusted strings in the app — straight from NWS — and Svelte's
   * interpolation escaping them is the whole defence. There is no `{@html}`
   * here and `web/tests/panels.test.ts` asserts it.
   *
   * Stacked alerts — a tornado warning inside a severe thunderstorm watch
   * inside a flood watch — are listed by the menu, which is where the choice
   * of which one to read belongs. This shows the one that was chosen, in
   * round 2's frame (Stage 10.5, 2d): `TextPopup`.
   */

  interface Props {
    feature: AlertRef
    onClose: () => void
  }

  let { feature, onClose }: Props = $props()

  let alert = $state<Alert | null>(null)
  let error = $state('')

  $effect(() => {
    const wanted = feature
    // What the bundle already said, until the server says it again. The fetch
    // still runs: it is what knows whether the alert has since expired.
    alert = wanted.alert ?? null
    error = ''
    let cancelled = false
    void fetchAlert(wanted.capId)
      .then((a) => {
        if (!cancelled) alert = a
      })
      .catch((e: unknown) => {
        if (cancelled) return
        // The bundle's copy is already on screen, and its `expires` still
        // decides "no longer active" below. Only a 404 has more to say.
        if (wanted.alert && !(e instanceof ApiError && e.status === 404)) return
        // NWS keeps serving an alert after it lapses, so a 404 is rare and
        // `expired` below is the usual way to find out. Off the owner's VPN a
        // notification still opens the app, and the server is what is gone.
        error =
          e instanceof ApiError
            ? e.status === 404
              ? 'This alert is no longer active.'
              : `Couldn't load this alert (HTTP ${e.status}).`
            : "Can't reach the server to load this alert."
      })
    return () => {
      cancelled = true
    }
  })

  let expires = $derived(alert?.expires ?? feature.expiration ?? null)
  let expired = $derived(expires !== null && Date.parse(expires) < Date.now())

  let title = $derived(alert?.event ?? feature.prodType ?? 'Weather alert')
  let level = $derived(alertLevel(alert?.event ?? feature.prodType, alert?.description))

  const when = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  })

  function at(iso: string | null | undefined): string {
    const t = iso ? Date.parse(iso) : NaN
    return Number.isFinite(t) ? when.format(t) : ''
  }

  /** Issued, expires, severity and urgency: whichever of them NWS stated. */
  let meta = $derived(
    [
      { label: 'Issued', value: at(alert?.sent ?? alert?.effective) },
      { label: expired ? 'Expired' : 'Expires', value: at(expires), class: 'expires' },
      { label: 'Severity', value: alert?.severity ?? '' },
      { label: 'Urgency', value: alert?.urgency ?? '' },
    ].filter((m) => m.value && m.value !== 'Unknown'),
  )

  let sections = $derived(
    [
      { heading: 'Description', paras: paragraphs(alert?.description) },
      { heading: 'Instructions', paras: paragraphs(alert?.instruction) },
    ].filter((s): s is PopupSection => s.paras.length > 0),
  )

  let notice = $derived(
    expired && !error
      ? `This alert is no longer active. It expired ${at(expires)} and is shown for reference.`
      : '',
  )
</script>

<TextPopup
  label="Weather alert"
  closeLabel="Close alert"
  tag={expired ? 'Expired' : level.tag}
  tone={expired ? 'outline' : level.tone}
  issuer={alert?.sender ?? ''}
  {title}
  {meta}
  areas={areaList(alert?.area)}
  {notice}
  headline={alert?.headline ?? ''}
  {sections}
  source={`api.weather.gov${feature.capId ? ` · ${capTail(feature.capId)}` : ''}`}
  {onClose}
>
  {#if !alert && !error}
    <p class="status">Loading the full text…</p>
  {/if}
  {#if error}<p class="error">{error}</p>{/if}
</TextPopup>

<style>
  .status {
    margin: 0;
    font-size: 14px;
    color: var(--color-muted);
  }

  .error {
    margin: 0;
    font-size: 14px;
  }
</style>
